import { ActivityV2 } from 'models/activityV2'
import {
  NoteEventCard,
  ReminderEventCard,
  SampleEventCard,
  GenericEventCard,
  EmailEventCard,
  DocumentEventCard,
  CaptureEventCard,
} from './EventCards'

interface EventCardProps {
  event: ActivityV2
  onPreviewRequest?: (sampleId: number) => void
  onEmailClick?: (event: ActivityV2) => void
}

/**
 * Router component that delegates to specialized event card components.
 */
export function EventCard({
  event,
  onPreviewRequest,
  onEmailClick,
}: EventCardProps) {
  // Check if this is a note event
  const isNoteEvent =
    event.content_type.model === 'companynote' ||
    event.content_type.model === 'contactnote' ||
    event.content_type.model === 'doornote' ||
    event.content_type.model === 'dealnote'

  // Check if this is a reminder event
  const isReminderEvent = event.content_type.model === 'reminder'

  // Check if this is a sample request event (CREATE or UPDATE)
  const isSampleEvent = event.content_type.model === 'samplerequest'

  // Check if this is an email event - only use custom card for CREATE events with data
  const isEmailEvent =
    event.content_type.model === 'emailmessage' &&
    event.action_type === 'CREATE' &&
    event.field_history_objects &&
    event.field_history_objects.length > 0

  // Check if this is a document event
  const isDocumentEvent = event.content_type.model === 'document'

  // Check if this is a capture submission event. Post-split the record is
  // engagements.Capture (was capture_forms.CapturedRecord / the unified
  // engagements.Activity); match the new content type.
  const isCaptureEvent =
    event.content_type.app_label === 'engagements' &&
    event.content_type.model === 'capture'

  // Route to appropriate specialized card
  if (isNoteEvent) {
    return <NoteEventCard event={event} />
  }

  if (isReminderEvent) {
    return <ReminderEventCard event={event} />
  }

  if (isSampleEvent) {
    return <SampleEventCard event={event} onPreviewRequest={onPreviewRequest} />
  }

  if (isEmailEvent) {
    return <EmailEventCard event={event} onEmailClick={onEmailClick} />
  }

  if (isDocumentEvent) {
    return <DocumentEventCard event={event} />
  }

  if (isCaptureEvent) {
    return <CaptureEventCard event={event} />
  }

  // Default to generic card for all other event types
  return <GenericEventCard event={event} />
}
